import { useEffect, useRef } from "react";
import { useFormContext } from "@/contexts/form";
import { useCountrySettings } from "@/hooks/use-app-country";
import { CachedLoanApplicationResponseBody } from "@/types/cached-loan-application";
import { cachedLoanApplicationToForms } from "@/utils/mapping/map-cached-loan-application-to-forms";
import { mergeDefined } from "@/utils/merge-defined";

export const useInitializeForms = (
	cachedData?: CachedLoanApplicationResponseBody
) => {
	const initializedCacheKey = useRef<string | undefined>(undefined);

	const landingForm = useFormContext("landingPageForm");
	const personalInfoForm = useFormContext("personalInformationForm");
	const aboutLoanForm = useFormContext("aboutForm");
	const economyForm = useFormContext("economyForm");
	const { validationCountry } = useCountrySettings();

	useEffect(() => {
		if (!cachedData) return;

		const cacheKey = JSON.stringify(cachedData);
		if (initializedCacheKey.current === cacheKey) return;

		const {
			landingPageFormData,
			aboutFormData,
			economyFormData,
			personalInformationFormData,
		} = cachedLoanApplicationToForms(cachedData, validationCountry);

		landingForm.reset(
			mergeDefined(landingForm.getValues(), landingPageFormData)
		);
		aboutLoanForm.reset(
			mergeDefined(aboutLoanForm.getValues(), aboutFormData)
		);
		personalInfoForm.reset(
			mergeDefined(
				personalInfoForm.getValues(),
				personalInformationFormData
			)
		);
		economyForm.reset(
			mergeDefined(economyForm.getValues(), economyFormData)
		);

		initializedCacheKey.current = cacheKey;
	}, [
		aboutLoanForm,
		cachedData,
		economyForm,
		landingForm,
		personalInfoForm,
		validationCountry,
	]);
};
